import { Box, Stack } from '@mui/material'
import { useEffect, useState } from 'react'

import { AxiosError } from 'axios'
import {
	getDatesArray,
	getDaysBetweenDates,
	roundingNumber,
} from 'components/shared/processes'
import { LicensesType } from 'models/IUserData'
import moment from 'moment'
import GroupsApiService from 'services/groupsApiService'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'

import ExpandableCard from 'ui/expandable-card/ExpandableCard'
import Preloader from 'ui/Preloader'
import { TypographyPrimary12 } from 'ui/typography/TypographyPrimary12'
import { TypographyPrimary16Bold } from 'ui/typography/TypographyPrimary16Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { TypographySecondaryBold16 } from 'ui/typography/TypographySecondaryBold16'
import { ExpiredLicense } from '../../../ui/expired-license/ExpiredLicense'
import GroupApexConsuption from './chart/GroupApexConsuption'
import { DateRestriction } from 'ui/DatePickerInput'
import DateRangePicker from 'ui/DatePickerInput/DateRangePicker'
interface GroupSpecificConsuptionChartProps {
	group_id: number
}
const GroupSpecificConsuptionChart = (
	props: GroupSpecificConsuptionChartProps,
) => {
	const [fetchData, setFetchData] = useState([])
	const [distributedFood, setDistributedFoog] = useState(0)
	const [consuptionFood, setConsuptionFood] = useState(0)
	const [loading, setLoading] = useState<boolean>(false)
	const [allDataIsNull, setAllDataIsNull] = useState(false)
	const [errorData, setErrorData] = useState<AxiosError>(null)
	const { userData } = useAppSelector(state => state.userReducer)
	const licenseInfo = userData?.company?.licenses?.find(
		el => el.license_codename === LicensesType.FOOD_TABLE,
	)
	const weekDays = getDatesArray(6)

	const [dateRange, setDateRange] = useState({
		startDate: weekDays.startDate,
		endDate: weekDays.endDate,
	})
	const daysBetweenDates = getDaysBetweenDates(dateRange)

	const onChangeCallback = value => {
		setDateRange({
			startDate: value[0],
			endDate: value[1],
		})
	}
	function getUpdatedData(fetchData) {
		const updatedData = []
		const startDateMillis = dateRange.startDate
		const endDateMillis = dateRange.endDate
		const dayInMillis = 24 * 60 * 60 * 1000

		for (
			let date = startDateMillis;
			date <= endDateMillis;
			date += dayInMillis
		) {
			const existingData = fetchData.find(item => item.date === date)
			if (existingData) {
				updatedData.push({ ...existingData })
			} else {
				updatedData.push({
					cleaned_sum: null,
					date,
					distributed_sum: null,
				})
			}
		}

		return updatedData
	}
	useEffect(() => {
		setLoading(true)
		setErrorData(null)
		GroupsApiService.getDataForConsuptionChart(
			props.group_id,
			dateRange.startDate,
			dateRange.endDate,
		)
			.then(response => {
				const data = response.data[0].dots.map(item => ({
					...item,
					date: moment(item.date).valueOf(),
				}))
				const updatedData = getUpdatedData(data)
				setAllDataIsNull(
					updatedData.every(
						el => el.distributed_sum === null && el.cleaned_sum === null,
					),
				)
				setFetchData(updatedData)

				setDistributedFoog(
					Number(
						data
							.reduce(
								(total, obj) =>
									total +
									(obj.distributed_sum === null ? 0 : obj.distributed_sum),
								0,
							)
							.toFixed(0),
					),
				)

				setConsuptionFood(
					Number(
						data
							.reduce(
								(total, obj) =>
									total + (obj.cleaned_sum === null ? 0 : obj.cleaned_sum),
								0,
							)
							.toFixed(0),
					),
				)

				setLoading(false)
			})
			.catch(error => {
				setErrorData(error)
			})
			.finally(() => setLoading(false))
	}, [dateRange])

	return (
		<ExpandableCard title={'Расход корма и остатки'} hideToggle>
			<Stack direction={'column'} gap={'24px'} sx={{ mt: '24px' }}>
				<DateRangePicker
					onChangeUpdateDate={onChangeCallback}
					startDate={dateRange.startDate}
					endDate={dateRange.endDate}
					weekFilter={true}
					monthFilter={true}
					yearFilter={true}
					currentRange={'Неделя'}
					restrictionType={DateRestriction.PAST}
				/>
				{loading ? (
					<Stack
						direction={'column'}
						alignItems={'center'}
						justifyContent={'center'}
						sx={{ height: '400px' }}
					>
						<Preloader />
					</Stack>
				) : errorData?.response?.status === 450 ? (
					<Stack
						sx={{ minHeight: '240px' }}
						direction={'column'}
						justifyContent={'center'}
						alignItems={'center'}
					>
						<ExpiredLicense
							availableLicenceDates={`Доступен просмотр данных за период ${moment(
								licenseInfo.start_dt,
							).format('DD.MM.YY')} - ${moment(licenseInfo.until_dt).format(
								'DD.MM.YY',
							)}`}
						/>
					</Stack>
				) : (
					<>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								gap: '60px',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '4px',
								}}
							>
								<TypographySecondary>Итого за период</TypographySecondary>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
										gap: '4px',
									}}
								>
									<TypographyPrimary16Bold>
										{distributedFood}
									</TypographyPrimary16Bold>
									<TypographyPrimary12>кг</TypographyPrimary12>
								</Box>
								<TypographySecondary>Роздано корма</TypographySecondary>
							</Box>

							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '4px',
									pl: 2,
								}}
							>
								<TypographySecondary>Итого за период</TypographySecondary>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
										gap: '4px',
									}}
								>
									<TypographyPrimary16Bold>
										{consuptionFood}
									</TypographyPrimary16Bold>
									<TypographyPrimary12>кг</TypographyPrimary12>
									<TypographyPrimary16Bold sx={{ color: '#7f7f7f' }}>
										/
									</TypographyPrimary16Bold>
									<TypographyPrimary16Bold>
										{consuptionFood == 0 || distributedFood == 0
											? 0
											: roundingNumber(
													(consuptionFood / distributedFood) * 100,
													1,
												)}
									</TypographyPrimary16Bold>
									<TypographyPrimary12>%</TypographyPrimary12>
								</Box>
								<TypographySecondary>Остаток</TypographySecondary>
							</Box>
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
							}}
						>
							<Box
								sx={{
									height: '250px',
									width: '100%',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									marginTop: '24px',
								}}
							>
								{fetchData.length > 0 && !allDataIsNull ? (
									<GroupApexConsuption
										fetchData={fetchData}
										daysBetweenDates={daysBetweenDates}
									/>
								) : (
									<Box
										sx={{
											mt: 'auto',
											mb: 'auto',
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'center',
											alignItems: 'center',
										}}
									>
										<TypographySecondaryBold16>
											Нет данных
										</TypographySecondaryBold16>
										<TypographySecondary14 sx={{ marginTop: '12px' }}>
											Нет данных, соответствующих заданным условиям.
										</TypographySecondary14>
									</Box>
								)}
							</Box>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									marginTop: '24px',
								}}
							>
								<span
									style={{
										width: '16px',
										height: '16px',
										borderRadius: '50%',
										backgroundColor: COLORS.main,
									}}
								></span>
								<TypographySecondary sx={{ marginLeft: '8px' }}>
									Роздано корма
								</TypographySecondary>

								<span
									style={{
										width: '16px',
										height: '16px',
										borderRadius: '50%',
										backgroundColor: COLORS.error,
										marginLeft: '16px',
									}}
								></span>
								<TypographySecondary sx={{ marginLeft: '8px' }}>
									Остаток
								</TypographySecondary>

								<span
									style={{
										width: '16px',
										height: '16px',
										borderRadius: '50%',
										backgroundColor: COLORS.inputBorder,
										marginLeft: '16px',
									}}
								></span>
								<TypographySecondary sx={{ marginLeft: '8px' }}>
									Нет данных
								</TypographySecondary>
							</Box>
						</Box>
					</>
				)}
			</Stack>
		</ExpandableCard>
	)
}

export default GroupSpecificConsuptionChart

GroupSpecificConsuptionChart.displayName = 'GroupSpecificConsuptionChart'
